import type { VerticalLandingContent } from '@nexofacil/schemas';
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode } from 'react';
import type { GetBrokerResponse, PublicBrokerSummary } from '@/lib/api';
import { brokerAvatarClass } from '@/lib/broker-avatar';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PropertyCard } from '@/components/property-card';
import {
  NoFilterMatch,
  PropertyFilterBar,
  usePropertyFilters,
} from '@/components/property-filters';
import { cn } from '@/lib/utils';
import type { LandingProps } from './types';

// =============================================================================
// MCMVLanding — design original do NexoFácil, usado por Angel/Wesley e
// qualquer corretor MCMV. Mantém visual histórico (hero centrado, grid de
// empreendimentos, FAQ MCMV). Brokers customizam via pageContent
// (headline, subheadline, theme, coverPhotoUrl, avatarShape).
// =============================================================================

export function MCMVLanding({ broker, properties, content, onCta }: LandingProps) {
  return (
    <div className="min-h-screen bg-background">
      <BrokerHeader broker={broker} content={content} onCta={onCta} />
      <main>
        <Hero
          broker={broker}
          content={content}
          onCta={onCta}
          hasProperties={properties.length > 0}
        />
        <HowItWorks content={content} />
        {/* Imóveis antes do "Sobre": no celular o corretor já aparece no header e no hero. */}
        {properties.length > 0 && <PropertiesSection content={content} properties={properties} />}
        <AboutBroker broker={broker} content={content} />
        <FAQSection content={content} />
        <FinalCTA content={content} onCta={onCta} />
      </main>
      <SiteFooter content={content} />
    </div>
  );
}

// ============================================================================
// Sections
// ============================================================================

function BrokerHeader({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-primary-foreground/10 bg-primary">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-3">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt=""
              className={brokerAvatarClass(
                broker.pageContent.avatarShape,
                'h-9 w-9 ring-2 ring-primary-foreground/30',
              )}
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-sm font-semibold text-primary-foreground">
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-sm font-semibold text-primary-foreground">{broker.name}</div>
            <div className="text-xs text-primary-foreground/75">{content.brokerSubtitle}</div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={onCta}
          className="h-11 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
        >
          {content.brokerHeaderCta}
        </Button>
      </div>
    </header>
  );
}

function Hero({
  broker,
  content,
  onCta,
  hasProperties,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
  hasProperties: boolean;
}) {
  const headline = broker.pageContent.heroHeadline ?? content.heroHeadlineDefault;
  const subheadline = broker.pageContent.heroSubheadline ?? content.heroSubheadlineDefault;
  const trustIcons: LucideIcon[] = [CheckCircle2, Clock, ShieldCheck];
  const coverUrl = broker.pageContent.coverPhotoUrl;

  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      {coverUrl ? (
        <>
          <img
            src={coverUrl}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[center_60%] opacity-50"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/70 to-primary/85" />
        </>
      ) : (
        <DecorBackground />
      )}
      <div className="container relative grid gap-10 py-16 md:grid-cols-[1.3fr_1fr] md:items-center md:py-24 lg:py-28">
        <div>
          <Badge variant="onDark" className="mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            {content.heroBadge}
          </Badge>

          <h1 className="text-balance text-4xl font-extrabold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
            {headline}
          </h1>

          <p className="mt-5 max-w-xl text-balance text-base text-primary-foreground/80 md:text-lg">
            {subheadline}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              size="lg"
              onClick={onCta}
              className="h-12 bg-primary-foreground px-7 text-base font-semibold text-primary shadow-lg shadow-black/20 hover:bg-primary-foreground/90"
            >
              {content.heroPrimaryCta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            {hasProperties && (
              <a
                href="#empreendimentos"
                className="inline-flex h-12 items-center justify-center rounded-md px-4 text-sm font-medium text-primary-foreground/80 transition hover:text-primary-foreground"
              >
                {content.heroSecondaryCta}
              </a>
            )}
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-primary-foreground/75">
            {content.trustBadges.slice(0, 3).map((label, i) => (
              <TrustItem key={label} icon={trustIcons[i] ?? CheckCircle2} label={label} />
            ))}
          </ul>
        </div>

        <BrokerCard broker={broker} content={content} onCta={onCta} />
      </div>
    </section>
  );
}

function BrokerCard({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  return (
    // No celular o header e o hero já apresentam o corretor; o card só repetiria.
    <div className="relative mx-auto hidden w-full max-w-sm rounded-3xl bg-primary-foreground/5 p-1 ring-1 ring-primary-foreground/10 md:mx-0 md:block">
      <div className="rounded-[calc(1.5rem-1px)] bg-primary/40 p-6 md:p-7">
        <div className="flex items-center gap-4">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt=""
              className={brokerAvatarClass(
                broker.pageContent.avatarShape,
                'h-16 w-16 ring-4 ring-primary-foreground/20',
              )}
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-foreground/10 text-2xl font-bold text-primary-foreground ring-4 ring-primary-foreground/20">
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-xs uppercase tracking-wider text-primary-foreground/75">
              {content.brokerCardLabel}
            </div>
            <div className="text-lg font-bold text-primary-foreground">{broker.name}</div>
          </div>
        </div>
        {broker.bio && (
          <p className="mt-5 text-sm leading-relaxed text-primary-foreground/75">{broker.bio}</p>
        )}
        <Button
          variant="outline"
          onClick={onCta}
          className="mt-5 h-11 w-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
        >
          {content.brokerCardCta}
        </Button>
      </div>
    </div>
  );
}

function TrustItem({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[hsl(var(--brand-accent))]" />
      <span>{label}</span>
    </li>
  );
}

function DecorBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 opacity-60">
      <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
      <div className="absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-white/5 blur-3xl" />
    </div>
  );
}

function HowItWorks({ content }: { content: VerticalLandingContent }) {
  return (
    <Section>
      <SectionHeader title={content.howItWorksTitle} subtitle={content.howItWorksSubtitle} />
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {content.howItWorksSteps.map((s, i) => (
          <div
            key={s.title}
            className="rounded-2xl border bg-card p-7 transition-shadow hover:shadow-md"
          >
            <div className="font-mono text-sm font-semibold text-primary">
              {String(i + 1).padStart(2, '0')}
            </div>
            <h3 className="mt-3 text-xl font-bold leading-tight text-foreground">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function AboutBroker({
  broker,
  content,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
}) {
  const about = broker.pageContent.aboutText;
  if (!about && !broker.bio) return null;

  return (
    <Section variant="muted">
      <div className="grid gap-10 md:grid-cols-[260px_1fr] md:items-start">
        <div className="flex justify-center md:block">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt={broker.name}
              className={cn(
                'h-48 w-48 rounded-3xl shadow-xl shadow-primary/10 md:h-60 md:w-60',
                broker.pageContent.avatarShape === 'square'
                  ? 'bg-white object-contain p-3'
                  : 'object-cover',
              )}
            />
          ) : (
            <div className="flex h-48 w-48 items-center justify-center rounded-3xl bg-primary text-6xl font-bold text-primary-foreground shadow-xl shadow-primary/10 md:h-60 md:w-60">
              {broker.name.charAt(0)}
            </div>
          )}
        </div>
        <div>
          <div className="text-sm font-semibold uppercase tracking-wider text-primary">
            {content.aboutTitle}
          </div>
          <h2 className="mt-2 text-3xl font-bold leading-tight md:text-4xl">{broker.name}</h2>
          {about ? (
            <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-muted-foreground">
              {about}
            </p>
          ) : broker.bio ? (
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">{broker.bio}</p>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

function PropertiesSection({
  content,
  properties,
}: {
  content: VerticalLandingContent;
  properties: GetBrokerResponse['properties'];
}) {
  const { filtered, filters } = usePropertyFilters(properties);
  return (
    <Section id="empreendimentos">
      <SectionHeader title={content.listingsTitle} subtitle={content.listingsSubtitle} />
      <PropertyFilterBar filters={filters} className="mt-10 justify-center" />
      {filtered.length === 0 ? (
        <NoFilterMatch filters={filters} />
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <PropertyCard key={p.id} property={p} />
          ))}
        </div>
      )}
    </Section>
  );
}

function FAQSection({ content }: { content: VerticalLandingContent }) {
  return (
    <Section variant="muted">
      <SectionHeader title={content.faqTitle} subtitle={content.faqSubtitle} />
      <div className="mx-auto mt-10 max-w-3xl">
        <Accordion type="single" collapsible className="w-full">
          {content.faqItems.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger>{item.q}</AccordionTrigger>
              <AccordionContent>{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}

function FinalCTA({ content, onCta }: { content: VerticalLandingContent; onCta: () => void }) {
  return (
    <section className="bg-primary text-primary-foreground">
      <div className="container py-20 text-center md:py-28">
        <h2 className="mx-auto max-w-2xl text-balance text-3xl font-extrabold leading-tight md:text-4xl">
          {content.finalCtaTitle}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-balance text-primary-foreground/80">
          {content.finalCtaSubtitle}
        </p>
        <Button
          size="lg"
          onClick={onCta}
          className="mt-8 h-12 bg-primary-foreground px-8 text-base font-semibold text-primary shadow-lg shadow-black/20 hover:bg-primary-foreground/90"
        >
          {content.finalCtaButton}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </section>
  );
}

function SiteFooter({ content }: { content: VerticalLandingContent }) {
  return (
    <footer className="border-t bg-background">
      <div className="container flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground sm:flex-row sm:gap-6">
        <div className="flex items-center gap-2">
          <img src="/nexofacil-icon.png" alt="" className="h-6 w-6 opacity-70" />
          <span>{content.footerLabel}</span>
        </div>
        <a
          href="/privacidade"
          className="inline-block py-3 text-xs underline hover:text-foreground"
        >
          Política de privacidade
        </a>
      </div>
    </footer>
  );
}

function Section({
  children,
  variant,
  id,
}: {
  children: ReactNode;
  variant?: 'muted';
  id?: string;
}) {
  return (
    <section
      id={id}
      className={
        variant === 'muted' ? 'bg-secondary/40 py-20 md:py-28' : 'bg-background py-20 md:py-28'
      }
    >
      <div className="container">{children}</div>
    </section>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <h2 className="text-balance text-3xl font-bold leading-tight md:text-4xl">{title}</h2>
      {subtitle && <p className="mt-3 text-balance text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
